import React, { useEffect, useRef, useState } from 'react';
import { formatShortDay } from 'pages/Fields/fields.utils';
import {
  Card,
  Stats,
  Stat,
  Value,
  Label,
  FieldButton,
  ImgWrap,
  NoImage,
  ImageLoader,
  Actions,
  ConfirmButton,
  RejectButton,
  GhostButton,
  ErrorText
} from './StoneViewer.styles';

const passDay = pass => (pass ? formatShortDay(pass.observed_on) : '—');

const isTyping = target => ['INPUT', 'TEXTAREA', 'SELECT'].includes(target?.tagName);

export const StoneViewer = ({
  stone,
  onPickField,
  onPrev,
  onNext,
  onConfirm,
  onReject,
  disablePrev,
  disableNext,
  loading,
  saveFailed
}) => {
  const src = stone.image_url;
  const [imgLoading, setImgLoading] = useState(Boolean(src));
  const [imgFailed, setImgFailed] = useState(false);
  const [isFullImg, setIsFullImg] = useState(false);
  const imgRef = useRef();

  useEffect(() => {
    setImgLoading(Boolean(src));
    setImgFailed(false);
    setIsFullImg(false);
  }, [src]);

  useEffect(() => {
    const events = {
      ArrowLeft: onPrev,
      ArrowUp: onPrev,
      ArrowRight: onNext,
      ArrowDown: onNext,
      KeyC: onConfirm,
      KeyR: onReject,
      KeyZ: () => setIsFullImg(prev => !prev)
    };
    const handleKeyDown = e => {
      if (!events[e.code] || isTyping(e.target)) return;
      e.preventDefault();
      events[e.code]();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onPrev, onNext, onConfirm, onReject]);

  return (
    <Card>
      <Stats>
        <Stat>
          <Value>{stone.size_cm == null ? '—' : `${stone.size_cm} cm`}</Value>
          <Label>
            {stone.size_class ? `class ${stone.size_class.toUpperCase()}` : 'no size yet'}
            {stone.size_is_mocked && ' · mocked size'}
          </Label>
        </Stat>
        <Stat>
          <Value>{stone.observation_count}</Value>
          <Label>observation{stone.observation_count === 1 ? '' : 's'}</Label>
        </Stat>
        <Stat>
          <Value>{passDay(stone.first_pass)}</Value>
          <Label>first seen</Label>
        </Stat>
        <Stat>
          <Value>{passDay(stone.last_pass)}</Value>
          <Label>last seen</Label>
        </Stat>
      </Stats>
      <Label>
        Stone #{stone.id} in{' '}
        <FieldButton
          type='button'
          onClick={() => onPickField({ id: stone.field.id, name: stone.field.name })}
        >
          {stone.field.name}
        </FieldButton>{' '}
        · {stone.user}
      </Label>
      {src && !imgFailed ? (
        <ImgWrap $full={isFullImg}>
          <img
            src={src}
            alt='Stone'
            ref={imgRef}
            onLoad={() => setImgLoading(false)}
            onError={() => {
              setImgLoading(false);
              setImgFailed(true);
            }}
          />
          {imgLoading && <ImageLoader>Loading image...</ImageLoader>}
        </ImgWrap>
      ) : (
        <NoImage>No image for this stone</NoImage>
      )}
      {saveFailed && <ErrorText>Could not save. The list was refreshed.</ErrorText>}
      <Actions>
        <GhostButton
          type='button'
          onClick={onPrev}
          disabled={disablePrev}
          aria-label='Previous'
        >
          ‹
        </GhostButton>
        <ConfirmButton
          type='button'
          onClick={onConfirm}
          disabled={loading || stone.review === 'confirmed'}
          title='C - to confirm'
        >
          Confirm
        </ConfirmButton>
        <GhostButton
          type='button'
          onClick={() => setIsFullImg(!isFullImg)}
          title='Z - to zoom'
        >
          Zoom {isFullImg ? 'out' : 'in'}
        </GhostButton>
        <RejectButton
          type='button'
          onClick={onReject}
          disabled={loading || stone.review === 'rejected'}
          title='R - to reject'
        >
          Reject
        </RejectButton>
        <GhostButton
          type='button'
          onClick={onNext}
          disabled={disableNext}
          aria-label='Next'
        >
          ›
        </GhostButton>
      </Actions>
    </Card>
  );
};
